<template>
	<div
		ref="trigger"
		role="combobox"
		:aria-expanded="isActive"
		:aria-haspopup="ariaOwns ? 'listbox' : undefined"
		:aria-owns="ariaOwns"
		class="cursor-default"
		@click.stop="toggle"
	>
		<slot name="trigger" :is-active="isActive" :open="open" :toggle="toggle" />
	</div>
	<Transition name="fade">
		<div
			v-if="isActive"
			ref="content"
			class="rounded-2 shadow-lg bg-neutral-25 overflow-hidden z-6"
			:class="[{ 'width-full': fullWidth }]"
			:style="floatingStyles"
			@click.stop
		>
			<slot :close="close" />
		</div>
	</Transition>
</template>

<script setup lang="ts">
	import { computed, ref } from 'vue'
	import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/vue'
	import { onClickOutside } from '@vueuse/core'

	type Padding = number | { top?: number; right?: number; bottom?: number; left?: number }

	const isActive = ref(false)
	const trigger = ref<HTMLElement>()
	const content = ref<HTMLElement>()
	const emit = defineEmits(['opened'])
	const placementMap = {
		start: 'bottom-start',
		end: 'bottom-end',
		center: 'bottom',
	} as const

	interface Props {
		fullWidth?: boolean
		ariaOwns?: string
		gap?: number
		padding?: Padding
		placement?: 'start' | 'end' | 'center'
	}

	const props = withDefaults(defineProps<Props>(), {
		ariaOwns: undefined,
		gap: 4,
		padding: 16,
		placement: 'start',
	})

	const open = () => {
		isActive.value = true
		emit('opened')
	}

	const close = () => {
		isActive.value = false
	}

	const toggle = () => {
		if (isActive.value) return close()
		open()
	}

	onClickOutside(content, () => close(), { ignore: [trigger] })

	const middleware = computed(() => [
		flip({ padding: props.padding }),
		shift({ padding: props.padding }),
		offset(props.gap),
		size({
			padding: props.padding,
			apply({ availableWidth, elements }) {
				Object.assign(elements.floating.style, { maxWidth: `${availableWidth}px` })
			},
		}),
	])

	const { floatingStyles } = useFloating(trigger, content, {
		placement: placementMap[props.placement],
		strategy: 'fixed',
		middleware,
		whileElementsMounted: autoUpdate,
	})
</script>
